import { IElementBounds, IOverlayUIConfig, IScreenCoords, IViewportBounds } from '@newroom-connect/library/interfaces';

export interface IHoverState {
  mode: boolean;
  aspect: boolean
}

/**
 * Static helper utilities for viewport operations.
 * Contains pure functions for viewport calculations and transformations.
 */
export class ViewportHelper {
  /**
   * Get viewport bounds from an HTML element.
   * @param element - The HTML element to get bounds from.
   * @returns Viewport bounds object.
   */
  public static getViewportBounds(element: HTMLElement): IViewportBounds {
    const rect = element.getBoundingClientRect();

    return { width: rect.width, height: rect.height };
  }

  /**
   * Get element bounds from an HTML element.
   * @param element - The HTML element to get bounds from.
   * @returns Element bounds object.
   */
  public static getElementBounds(element: HTMLElement): IElementBounds {
    const rect = element.getBoundingClientRect();

    return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
  }

  /**
   * Convert global screen coordinates to element-relative coordinates.
   * @param globalCoords - Global screen coordinates.
   * @param elementBounds - Element bounds to convert relative to.
   * @returns Element-relative screen coordinates.
   */
  public static globalToElementCoords(globalCoords: IScreenCoords, elementBounds: IElementBounds): IScreenCoords {
    return { x: globalCoords.x - elementBounds.x, y: globalCoords.y - elementBounds.y };
  }

  /**
   * Convert element-relative coordinates to global screen coordinates.
   * @param elementCoords - Element-relative coordinates.
   * @param elementBounds - Element bounds to convert from.
   * @returns Global screen coordinates.
   */
  public static elementToGlobalCoords(elementCoords: IScreenCoords, elementBounds: IElementBounds): IScreenCoords {
    return { x: elementCoords.x + elementBounds.x, y: elementCoords.y + elementBounds.y };
  }

  /**
   * Calculate aspect ratio from viewport bounds.
   * @param bounds - The viewport dimensions to calculate ratio from.
   * @returns Aspect ratio (width / height).
   */
  public static getAspectRatio(bounds: IViewportBounds): number {
    return bounds.height > 0 ? bounds.width / bounds.height : 1;
  }

  /**
   * Check if viewport bounds are valid (positive dimensions).
   * @param bounds - Viewport bounds to validate.
   * @returns True if bounds are valid.
   */
  public static isValidViewportBounds(bounds: IViewportBounds): boolean {
    return bounds.width > 0 && bounds.height > 0 && isFinite(bounds.width) && isFinite(bounds.height);
  }

  /**
   * Normalize screen coordinates to 0-1 range based on viewport bounds.
   * @param coords - Screen coordinates to normalize.
   * @param bounds - Viewport bounds for normalization.
   * @returns Normalized coordinates (0-1 range).
   */
  public static normalizeCoords(coords: IScreenCoords, bounds: IViewportBounds): IScreenCoords {
    return { x: bounds.width > 0 ? coords.x / bounds.width : 0, y: bounds.height > 0 ? coords.y / bounds.height : 0 };
  }

  /**
   * Denormalize coordinates from 0-1 range to screen coordinates.
   * @param normalizedCoords - Normalized coordinates (0-1 range).
   * @param bounds - Viewport bounds for denormalization.
   * @returns Screen coordinates.
   */
  public static denormalizeCoords(normalizedCoords: IScreenCoords, bounds: IViewportBounds): IScreenCoords {
    return { x: normalizedCoords.x * bounds.width, y: normalizedCoords.y * bounds.height };
  }

  /**
   * Calculate overlay handle positions from corner coordinates.
   *
   * @param corners - Array of corner coordinates in screen space.
   * @param targetBounds - Target element bounds to convert coordinates relative to.
   *
   * @returns Array of corner handle positions relative to target element.
   */
  public static calculateOverlayHandlePositions(corners: IScreenCoords[], targetBounds: IElementBounds): Array<{ id: string; x: number; y: number }> {
    if (corners.length !== 4) {
      return [];
    }

    const handleIds = ['tl', 'tr', 'bl', 'br'];

    return corners.map((corner, index) => ({
      id: handleIds[index],
      x: corner.x - targetBounds.x,
      y: corner.y - targetBounds.y
    }));
  }

  /**
   * Calculate menu position near top-right corner with offset.
   *
   * @param corners - Array of corner coordinates in screen space.
   * @param targetBounds - Target element bounds to convert coordinates relative to.
   * @param offset - Offset distance from corner.
   *
   * @returns Menu position relative to target element.
   */
  public static calculateMenuPosition(corners: IScreenCoords[], targetBounds: IElementBounds, offset: number): { x: number; y: number } {
    if (corners.length < 2) {
      return { x: 0, y: 0 };
    }

    const topRightCorner = corners[1];

    return {
      x: topRightCorner.x - targetBounds.x + offset,
      y: topRightCorner.y - targetBounds.y + offset
    };
  }

  /**
   * Calculate UI configuration for overlay controls.
   *
   * @param mode - Current resize mode.
   * @param isAspectLocked - Whether aspect ratio is locked.
   * @param hoverStates - Button hover states.
   *
   * @param hoverStates.mode
   * @param hoverStates.aspect
   * @returns UI configuration object with colors and classes.
   */
  public static calculateOverlayUIConfig(mode: string, isAspectLocked: boolean, hoverStates: IHoverState): IOverlayUIConfig {
    const isTransformMode = mode === 'TRANSFORM';

    const modeIconColor = isTransformMode ? 'cyan' : (hoverStates.mode ? 'cyan' : 'white');
    const aspectIconColor = isAspectLocked ? 'cyan' : (hoverStates.aspect ? 'cyan' : 'white');

    const modeBorderClass = isTransformMode ? 'border-cyan-500' : 'border-gray-600';
    const aspectBorderClass = isAspectLocked ? 'border-cyan-500' : 'border-gray-600';

    return {
      modeIconColor,
      aspectIconColor,
      modeBorderClass,
      aspectBorderClass
    };
  }

  /**
   * Check if transform should be stopped based on validation.
   *
   * @param corners - Array of corner coordinates to validate.
   * @param minArea - Minimum area threshold.
   *
   * @returns True if drag should be stopped.
   */
  public static shouldStopTransformDrag(corners: IScreenCoords[], minArea = 100): boolean {
    // Import CoordinateValidationHelper methods
    if (corners.length !== 4) {
      return true;
    }

    // Check if all corners are valid
    const allValid = corners.every(corner => typeof corner.x === 'number'
      && typeof corner.y === 'number'
      && !isNaN(corner.x)
      && !isNaN(corner.y)
      && isFinite(corner.x)
      && isFinite(corner.y)
    );

    if (!allValid) {
      return true;
    }

    // Calculate area using shoelace formula
    const orderedCorners = [corners[0], corners[1], corners[3], corners[2]];
    let area = 0;

    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;

      area += orderedCorners[i].x * orderedCorners[j].y;
      area -= orderedCorners[j].x * orderedCorners[i].y;
    }

    const calculatedArea = Math.abs(area) / 2;

    return calculatedArea < minArea;
  }
}
